import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { ScoreByMode } from '@sorare/core/src/components/scoring/ScoreByMode';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';

import { WithPlayerAppearancesCell } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/WithPlayerAppearancesCell/index.tsx';

import type { AveragePlayerScoreCell_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: AveragePlayerScoreCell_anyPlayer;
  mode: TotalAveragePlayerScore;
  scopedScore?: number | null;
};

export const AveragePlayerScoreCell = ({
  mode,
  anyPlayer,
  scopedScore,
}: Props) => {
  const { up: laptopOrAbove } = useScreenSize('laptop');

  return (
    <WithPlayerAppearancesCell anyPlayer={anyPlayer} mode={mode}>
      {scopedScore !== undefined ? (
        <ScoreByMode
          scores={
            { [mode]: scopedScore } as Record<
              TotalAveragePlayerScore,
              number | null
            >
          }
          mode={mode}
          size={!laptopOrAbove ? 3 : undefined}
          hideTooltip
        />
      ) : (
        <PlayerScore
          player={anyPlayer}
          mode={mode}
          small={!laptopOrAbove}
          hideTooltip
        />
      )}
    </WithPlayerAppearancesCell>
  );
};

AveragePlayerScoreCell.fragments = {
  anyPlayer: gql`
    fragment AveragePlayerScoreCell_anyPlayer on AnyPlayerInterface {
      slug
      ...PlayerScore_anyPlayer
      ...WithPlayerAppearancesCell_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
    ${WithPlayerAppearancesCell.fragments.anyPlayer}
  ` as TypedDocumentNode<AveragePlayerScoreCell_anyPlayer>,
};
